import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { Button, Form, FormControl, Modal, Spinner, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';

import { bootstrapDanger, disabledGray, standardGray } from 'lib/css/colors';

function ResegmentedClassificationModal({
  show,
  options,
  selectedClusters,
  classNamesByCluster,
  removeUnselectedClusters,
  loadingOptions,
  submitting,
  hasMissingClassName,
  onClose,
  onToggleCluster,
  onClassNameChange,
  onRemoveUnselectedClustersChange,
  onSubmit,
}) {
  const selectedClusterSet = useMemo(() => new Set(selectedClusters), [selectedClusters]);

  return (
    <Modal show={show} onHide={onClose} size="lg" centered animation={false}>
      <Modal.Header closeButton={!submitting}>
        <Modal.Title>Classify Re-Segmented Clusters</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className={css(styles.modalHelpText)}>
          Select the clusters to classify. By default, unselected clusters stay in the
          re-segmentation results after classification.
        </p>
        {loadingOptions ? (
          <div className={css(styles.modalLoading)}>
            <Spinner animation="border" size="sm" className="mr-2" />
            Loading classification options...
          </div>
        ) : options.length === 0 ? (
          <div className={css(styles.emptyState)}>No classification options found.</div>
        ) : (
          <Table bordered size="sm" className={css(styles.classificationTable)}>
            <thead>
              <tr>
                <th style={{ width: '12%' }}>Classify</th>
                <th style={{ width: '34%' }}>Cluster Name</th>
                <th>Cluster Class Name</th>
              </tr>
            </thead>
            <tbody>
              {options.map(option => (
                <tr key={option.cluster_name || 'missing-cluster'}>
                  <td className="text-center">
                    <Form.Check
                      type="checkbox"
                      checked={selectedClusterSet.has(option.cluster_name)}
                      disabled={submitting || !option.cluster_name}
                      onChange={() => onToggleCluster(option.cluster_name)}
                      aria-label={`Classify cluster ${option.cluster_name}`}
                    />
                  </td>
                  <td>{option.cluster_name || <span className={css(styles.na)}>Missing</span>}</td>
                  <td>
                    <FormControl
                      value={classNamesByCluster[option.cluster_name] || ''}
                      onChange={e => onClassNameChange(option.cluster_name, e.target.value)}
                      disabled={submitting || !option.cluster_name}
                      maxLength={512}
                      placeholder="Enter class name"
                      aria-label={`Class name for cluster ${option.cluster_name}`}
                      className={css(
                        styles.classNameInput,
                        !classNamesByCluster[option.cluster_name]?.trim() &&
                          styles.classNameInputMissing,
                      )}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {hasMissingClassName && (
          <div className={css(styles.classificationWarning)}>
            Classification is blocked until every selected cluster has a class name.
          </div>
        )}
        <Form.Check
          type="checkbox"
          id="remove-unselected-clusters"
          className={css(styles.removeUnselectedCheck)}
          checked={removeUnselectedClusters}
          disabled={submitting}
          onChange={e => onRemoveUnselectedClustersChange(e.target.checked)}
          label="Remove unselected clusters as not applicable after classification"
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={onSubmit}
          disabled={
            loadingOptions || submitting || hasMissingClassName || selectedClusters.length === 0
          }
        >
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="mr-2" />
              Classifying...
            </>
          ) : (
            'Classify Selected Clusters'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

const classificationOptionShape = PropTypes.shape({
  cluster_name: PropTypes.string,
  class_name: PropTypes.string,
});

ResegmentedClassificationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  options: PropTypes.arrayOf(classificationOptionShape).isRequired,
  selectedClusters: PropTypes.arrayOf(PropTypes.string).isRequired,
  classNamesByCluster: PropTypes.objectOf(PropTypes.string).isRequired,
  removeUnselectedClusters: PropTypes.bool.isRequired,
  loadingOptions: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  hasMissingClassName: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onToggleCluster: PropTypes.func.isRequired,
  onClassNameChange: PropTypes.func.isRequired,
  onRemoveUnselectedClustersChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

const styles = StyleSheet.create({
  modalHelpText: {
    fontSize: '13px',
    color: standardGray,
    marginBottom: '12px',
  },
  modalLoading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '120px',
    color: standardGray,
  },
  classificationTable: {
    fontSize: '13px',
    marginBottom: 0,
  },
  classNameInput: {
    fontSize: '13px',
  },
  classNameInputMissing: {
    borderColor: bootstrapDanger,
  },
  classificationWarning: {
    marginTop: '12px',
    color: bootstrapDanger,
    fontWeight: 600,
    fontSize: '13px',
  },
  removeUnselectedCheck: {
    marginTop: '12px',
    fontSize: '13px',
    color: standardGray,
  },
  na: {
    color: disabledGray,
    fontStyle: 'italic',
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '200px',
    color: standardGray,
    fontSize: '14px',
  },
});

export default ResegmentedClassificationModal;
